import React from 'react';
import './privacyPolicy.css';

const PrivacyPolicy = () => {
    return (
        <main className="page privacyPage">
            <div className="titlePage">
                <h1 className="heroes">Política de Privacidad</h1>
                <span className="privacyDate">Última actualización: 2026-04-21</span>
            </div>

            <aside className="privacyIntro">
                <p>Esta Política de Privacidad describe cómo <strong>Forallia</strong> (“nosotros”) trata los datos cuando utilizas <strong>Forallia</strong> y <strong>Forallia BOT Definitivo</strong> (en conjunto, “la Aplicación”), que se integran con plataformas externas como <strong>Twitch</strong>, <strong>YouTube</strong>, <strong>Instagram (Meta)</strong> y <strong>Discord</strong> para ofrecer funcionalidades de comunidad, chat en directo, recompensas y mecánicas de juego asociadas a la cuenta del usuario.</p>
            </aside>

            <section className="privacyCard">
                <h2>1. Responsable del tratamiento</h2>
                <p><strong>Forallia</strong><br/>
                    Correo de contacto: <a className="privacyLink" href="mailto:naruxforall@gmail.com">naruxforall@gmail.com</a></p>
            </section>

            <section className="privacyCard">
                <h2>2. Datos que tratamos</h2>
                <p>En función de las plataformas que vincules, tratamos las siguientes categorías de datos:</p>

                <h3 className="privacySubtitle">2.1 Datos de cuenta de Twitch</h3>
                <ul className="privacyList">
                    <li>Identificador de usuario de Twitch, nombre de usuario y nombre para mostrar.</li>
                    <li>Imagen de perfil pública.</li>
                    <li>Dirección de correo electrónico verificada de la cuenta de Twitch.</li>
                    <li>Token OAuth de acceso necesario para operar el bot en tu nombre.</li>
                    <li>Estado de suscripción, fecha y origen de la suscripción, recompensas asociadas.</li>
                    <li>Eventos de chat, seguidores y bits recibidos a través de las APIs y EventSub de Twitch (procesados en tiempo real).</li>
                </ul>

                <h3 className="privacySubtitle">2.2 Datos de cuenta de YouTube / Google</h3>
                <ul className="privacyList">
                    <li>Identificador del canal de YouTube e identificador único de la cuenta de Google (<em>sub</em>).</li>
                    <li>Nombre del canal e imagen de perfil pública.</li>
                    <li>Dirección de correo electrónico asociada a la cuenta de Google.</li>
                    <li>Tokens OAuth (acceso y refresco) necesarios para leer el chat en directo y mantener la sesión.</li>
                    <li>Mensajes y nombres de los participantes del chat en directo de YouTube (procesados en tiempo real para mecánicas de bot; no se almacenan a largo plazo).</li>
                </ul>

                <h3 className="privacySubtitle">2.3 Datos de Instagram (Meta Graph API)</h3>
                <ul className="privacyList">
                    <li>Identificadores proporcionados por Meta (<em>IGSID</em> del remitente, IDs de conversación y mensaje).</li>
                    <li>Contenido de los mensajes directos (DMs) que nos envías y metadatos permitidos por la API.</li>
                </ul>

                <h3 className="privacySubtitle">2.4 Datos de Discord</h3>
                <ul className="privacyList">
                    <li>Identificador de usuario de Discord e imagen de perfil pública.</li>
                    <li>Pertenencia a servidores y roles necesarios para asignar recompensas o permisos.</li>
                </ul>

                <h3 className="privacySubtitle">2.5 Datos generados por el uso de la Aplicación</h3>
                <ul className="privacyList">
                    <li>Saldos y monedas internas (<em>birux</em>, <em>biruxPlata</em>, <em>biruxOro</em>, <em>biruxComodin</em>, <em>overbox</em>).</li>
                    <li>Estadísticas de juego: nivel, experiencia, puntos de vida, ataque, agilidad, velocidad, facción y karma.</li>
                    <li>Puntos de actividad y de sorteos, rachas diarias y marcadores de eventos.</li>
                    <li>Preferencias de notificaciones y alertas configuradas por el usuario.</li>
                    <li>Si activas las notificaciones en Ajustes: la suscripción push de cada navegador en el que las actives (dirección del servicio de notificaciones del navegador y sus claves de cifrado).</li>
                    <li>Registros operativos mínimos (logs) necesarios para depuración y prevención de abuso.</li>
                </ul>
            </section>

            <section className="privacyCard">
                <h2>3. Finalidades y base legal</h2>
                <ul className="privacyList">
                    <li><strong>Prestación del servicio:</strong> autenticarte, vincular tus cuentas externas, operar bots de chat, otorgar recompensas y mantener tus progresos. Base legal: <em>ejecución de contrato</em>.</li>
                    <li><strong>Atención y automatización de mensajes</strong> (DMs de Instagram, chat de Twitch y YouTube): <em>interés legítimo</em> y/o <em>ejecución de contrato</em> cuando contactas voluntariamente.</li>
                    <li><strong>Seguridad y prevención de abuso:</strong> detección de uso indebido o automatizado. Base legal: <em>interés legítimo</em>.</li>
                    <li><strong>Comunicaciones operativas</strong> (avisos de expiración de tokens, alertas técnicas al administrador): <em>interés legítimo</em>.</li>
                    <li><strong>Avisos de códigos nuevos, de directos y de la Arena</strong>, por notificación del navegador y, si lo eliges, por correo electrónico: solo si los activas en Ajustes, y puedes desactivarlos ahí en cualquier momento. Base legal: <em>consentimiento</em>.</li>
                </ul>
            </section>

            <section className="privacyCard">
                <h2>4. Cómo obtenemos los datos</h2>
                <p>Los datos se obtienen exclusivamente:</p>
                <ul className="privacyList">
                    <li>Directamente del usuario al iniciar sesión y autorizar el acceso mediante OAuth en Twitch, YouTube/Google, Discord o Meta.</li>
                    <li>De las APIs oficiales de Twitch, YouTube Data API v3, Instagram Graph API y Discord, dentro del ámbito de los permisos que el usuario concede.</li>
                    <li>De la propia interacción del usuario con la Aplicación (mensajes, comandos, participación en eventos).</li>
                </ul>
            </section>

            <section className="privacyCard">
                <h2>5. Almacenamiento y conservación</h2>
                <p>Los datos persistentes se almacenan en una base de datos <strong>MongoDB</strong> bajo nuestro control. Los tokens OAuth se conservan únicamente mientras la integración con la plataforma correspondiente esté activa. El resto de datos se conserva el tiempo necesario para prestar el servicio y cumplir obligaciones legales, o hasta que el usuario solicite su eliminación.</p>
            </section>

            <section className="privacyCard">
                <h2>6. Compartición con terceros</h2>
                <p>No vendemos ni cedemos tus datos personales a terceros con fines comerciales. Tus datos son tratados exclusivamente por Forallia y por los proveedores estrictamente necesarios para operar el servicio:</p>
                <ul className="privacyList">
                    <li><strong>Twitch (Amazon)</strong>, <strong>YouTube / Google</strong>, <strong>Meta (Instagram)</strong> y <strong>Discord</strong>: como plataformas origen de los datos a través de sus APIs oficiales.</li>
                    <li><strong>Proveedor de correo (SMTP/Gmail)</strong>: para alertas técnicas administrativas y para enviarte por correo los avisos que hayas activado en Ajustes.</li>
                    <li><strong>Servicio de notificaciones de tu navegador</strong> (Google, Mozilla, Microsoft o Apple, según el navegador): entrega las notificaciones que actives. El contenido viaja cifrado y solo tu navegador puede leerlo.</li>
                </ul>
            </section>

            <section className="privacyCard">
                <h2>7. Derechos de los usuarios</h2>
                <p>Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad enviando un correo a <a className="privacyLink" href="mailto:naruxforall@gmail.com">naruxforall@gmail.com</a>. También puedes presentar una reclamación ante la autoridad de control competente en materia de protección de datos.</p>
                <p>Asimismo, puedes revocar en cualquier momento los permisos OAuth concedidos desde la configuración de tu cuenta en Twitch, Google, Meta o Discord.</p>
            </section>

            <section className="privacyCard">
                <h2>8. Seguridad</h2>
                <p>Aplicamos medidas técnicas y organizativas razonables (control de acceso, cifrado en tránsito, almacenamiento restringido de tokens y registros de auditoría) para proteger los datos frente a accesos no autorizados, pérdida o alteración.</p>
            </section>

            <section className="privacyCard">
                <h2>9. Servicios de terceros</h2>
                <p>La Aplicación se integra con plataformas de terceros (Twitch, YouTube/Google, Instagram/Meta, Discord). El uso de cada plataforma se rige adicionalmente por sus respectivos términos y políticas de privacidad, que recomendamos revisar.</p>
            </section>

            <section className="privacyCard">
                <h2>10. Menores de edad</h2>
                <p>La Aplicación no está dirigida a menores que no cumplan la edad mínima exigida por la plataforma de origen (Twitch, YouTube, Instagram o Discord). Si detectamos una cuenta perteneciente a un menor sin autorización, procederemos a eliminarla.</p>
            </section>

            <section className="privacyCard">
                <h2>11. Cambios en esta política</h2>
                <p>Esta política puede actualizarse para reflejar nuevas integraciones (por ejemplo, futuras incorporaciones a YouTube u otras plataformas) o cambios legales. Publicaremos cualquier cambio en esta página con una nueva fecha de actualización.</p>
            </section>

            <section className="privacyCard">
                <h2>Eliminación de datos</h2>
                <p>Para solicitar el borrado de tus datos, visita nuestra página dedicada:</p>
                <a className="privacyCta" href="https://forallia.com/dataDeletion">Página de eliminación de datos</a>
            </section>

            <footer className="privacyFooter">
                © 2025 Forallia
            </footer>
        </main>
    );
};

export default PrivacyPolicy;
